import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectFinalRound,
  selectFortressesUnderConstruction,
  selectGameResults,
  selectGoblinsTraps,
  selectGridConfigState,
  selectHexes,
  selectIsGameStarted,
  selectIsInitiativeInterceptedWithIsolationism,
  selectLeprechaunsStealCoinsActions,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectPendingManeuverActions,
  selectPendingMovementActions,
  selectPendingShootingActions,
  selectPendingSupply,
  selectPhase,
  selectPhaseCompletedPlayers,
  selectPlayers,
  selectPolicyAgrarianism,
  selectRound,
  selectSharedGameState,
  selectSpecialBuildings,
  selectUnits,
  selectWarPhaseStage
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {getPhaseProgress} from "./getPhaseProgress";
import {memoizeByRefs} from "./memoizeByRefs";
import {selectSkippedUnitIds} from "./unitTurnSkips";

// Срезы состояния, от которых зависит подсчет. Тик таймера (turnEndTimestamp)
// их не меняет — пересчета нет
const PROGRESS_INPUTS = [
  selectIsGameStarted,
  selectGameResults,
  selectRound,
  selectFinalRound,
  selectPhase,
  selectWarPhaseStage,
  selectPlayers,
  (state) => selectSharedGameState(state).currentPlayerIndex,
  (state) => selectSharedGameState(state).isParallelSupply,
  selectPhaseCompletedPlayers,
  selectUnits,
  selectHexes,
  selectGridConfigState,
  selectSpecialBuildings,
  selectBuildingsDamage,
  selectCitiesCache,
  selectGoblinsTraps,
  // Сырое поле: селектор подставляет новый [] вместо пустого значения
  (state) => selectSharedGameState(state).goblinsTrapStageTriggers,
  selectFortressesUnderConstruction,
  selectPendingSupply,
  selectPolicyAgrarianism,
  selectPendingBuildings,
  selectNextInitiativePlayerId,
  selectIsInitiativeInterceptedWithIsolationism,
  selectPendingMovementActions,
  selectLeprechaunsStealCoinsActions,
  selectPendingShootingActions,
  selectPendingManeuverActions,
];

// Пропуски хода — свой вход: они хранятся только у клиента и меняют N и список
// способных, но не S
const computePhaseProgress = memoizeByRefs(
  (state, playerId) => [
    playerId,
    selectSkippedUnitIds(state, playerId),
    ...PROGRESS_INPUTS.map((selectInput) => selectInput(state)),
  ],
  (state, playerId) => getPhaseProgress(state, playerId, {skippedUnitIds: selectSkippedUnitIds(state, playerId)})
);

// Запомненное S — число дел на начало своего этапа. Живет только в памяти
// клиента: в состояние партии и запись матча не попадает. Хранятся только
// этапы текущего раунда, фазы и этапа войны
let rememberedTotals = {stageKey: null, totalsByPlayerId: new Map()};

const rememberTotal = (state, playerId, progress) => {
  const stageKey = `${selectRound(state)}|${progress.phase}|${progress.warPhaseStage}`;
  if (rememberedTotals.stageKey !== stageKey) {
    rememberedTotals = {stageKey, totalsByPlayerId: new Map()};
  }

  const remembered = rememberedTotals.totalsByPlayerId.get(playerId);
  // Начало этапа или первый подсчет после перезагрузки посреди этапа. S не
  // меньше, чем дел на текущем состоянии (progress.total)
  const total = remembered === undefined ? progress.total : Math.max(remembered, progress.total);
  rememberedTotals.totalsByPlayerId.set(playerId, total);
  return total;
};

const withRememberedTotal = memoizeByRefs(
  (state, playerId, progress) => [progress, playerId],
  (state, playerId, progress) => {
    if (progress.done === null) {
      return progress;
    }
    const total = rememberTotal(state, playerId, progress);
    return total === progress.total ? progress : {...progress, total};
  }
);

/**
 * Дела этапа игрока для интерфейса: getPhaseProgress с мемоизацией по ссылкам
 * срезов состояния и запомненным S (total) на начало этапа — S не меняется до
 * конца этапа, даже если цель второго стрелка уничтожил первый.
 */
export const selectPhaseProgress = (state, playerId) => (
  withRememberedTotal(state, playerId, computePhaseProgress(state, playerId))
);
